/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import type { MixDifference } from "src/store/api/generatedApi";
import { useGetPlanAncestorsQuery } from "src/store/api/generatedApi";
import { buildMixIdChain } from "components/common/planHistory/mixAncestorChain";

export interface ChangeComment {
  comment: string;
  changedByName: string;
  changedAt: string;
  materialDifferences: MixDifference[];
}

export const useMixChangeComments = (planId: number, mixId: number) => {
  const tenantName = useTenant();
  const { data, isLoading } = useGetPlanAncestorsQuery({ tenantName, planId });

  const comments = useMemo((): ChangeComment[] => {
    if (data === undefined) return [];

    const lastDeploymentIndex = data.ancestors.reduce(
      (lastIdx, ancestor, i) => {
        const deployment = ancestor.target_deployments?.[0];
        return deployment &&
          !["edited_standard", "edited_backup"].includes(
            deployment.deployment_type
          )
          ? i
          : lastIdx;
      },
      -1
    );

    const postDeploymentAncestors = data.ancestors.slice(
      lastDeploymentIndex + 1
    );

    // Build mix_id chain for post-deployment ancestors. If the oldest source
    // appears in the chain, the mix was reverted — truncate to keep only the
    // IDs after the revert.
    const fullChain = buildMixIdChain(postDeploymentAncestors, mixId);
    const cycleIndex = fullChain.targets.indexOf(fullChain.currentMixId);
    const currentChainIds = new Set(
      cycleIndex === -1
        ? fullChain.targets
        : fullChain.targets.slice(0, cycleIndex)
    );
    if (currentChainIds.size === 0) return [];

    return postDeploymentAncestors
      .filter((ancestor) => {
        if (!ancestor.target_comment) return false;
        return ancestor.differences.some((mixDiff) =>
          currentChainIds.has(mixDiff.target_mix_id)
        );
      })
      .map((ancestor) => ({
        comment: ancestor.target_comment ?? "",
        changedByName: ancestor.target_created_by_name,
        changedAt: ancestor.target_created_at,
        materialDifferences: ancestor.differences
          .filter((mixDiff) => currentChainIds.has(mixDiff.target_mix_id))
          .flatMap((mixDiff) => mixDiff.differences)
          .filter((diff) => Math.abs(diff.value_difference) >= 0.1),
      }))
      .reverse();
  }, [data, mixId]);

  return { comments, isLoading };
};
